import Vue from 'vue'
import api from '../../services/api'
import { uuid } from './helpers'
import { imageTypes, imageEndpoint, releaseImage } from './images'

const fresh = () => ({ open: false, connectedOpen: false, conversations: [], directory: [], activeId: null, messages: {}, drafts: {}, resourceDrafts: {}, imageDrafts: {}, pending: {}, removedIds: {}, removalVersion: 0, unreadTotal: 0, loading: false, hasMore: {}, before: {}, inboxMore: false, inboxOffset: 0, generation: 0, settings: { retention_days: 183, max_images: 10, image_max_kb: 10240 } })

export const sortConversations = items => items.sort((a, b) => Number(!!b.is_favorite) - Number(!!a.is_favorite) || String(b.updated_at || b.last_message?.created_at || '').localeCompare(String(a.updated_at || a.last_message?.created_at || '')) || b.id - a.id)

export default {
  namespaced: true,
  state: fresh(),
  getters: { active: state => state.conversations.find(c => c.id === state.activeId) || null, activeMessages: state => state.messages[state.activeId] || [] },
  mutations: {
    reset(state) { Object.values(state.imageDrafts).flat().forEach(releaseImage); Object.assign(state, { ...fresh(), generation: state.generation + 1 }) },
    set(state, values) { Object.assign(state, values); if (values.conversations) sortConversations(state.conversations) },
    conversation(state, item) {
      if (state.removedIds[item.id]) return
      const index = state.conversations.findIndex(c => c.id === item.id)
      if (index >= 0) Vue.set(state.conversations, index, item)
      else state.conversations.unshift(item)
      sortConversations(state.conversations)
    },
    messages(state, { id, data, older }) {
      if (state.removedIds[id]) return
      const existing = state.messages[id] || []
      const items = older ? [...data.messages, ...existing] : [...existing, ...data.messages]
      const unique = Array.from(new Map(items.map(m => [m.id, m])).values()).sort((a, b) => a.id - b.id)
      Vue.set(state.messages, id, unique)
      if (older || !existing.length) {
        Vue.set(state.hasMore, id, data.has_more)
        Vue.set(state.before, id, data.next_before)
      }
    },
    message(state, message) {
      if (state.removedIds[message.conversation_id]) return
      const messages = [...(state.messages[message.conversation_id] || [])]
      const index = messages.findIndex(m => m.id === message.id || m.client_uuid === message.client_uuid)
      if (index >= 0) messages[index] = message
      else messages.push(message)
      Vue.set(state.messages, message.conversation_id, messages.sort((a, b) => a.id - b.id))
    },
    draft(state, { id, text }) { Vue.set(state.drafts, id, text) },
    resourceDrafts(state, { id, resources }) { Vue.set(state.resourceDrafts, id, resources) },
    imageDrafts(state, { id, images }) { Vue.set(state.imageDrafts, id, images) },
    imageDraft(state, { id, key, values }) { const image = (state.imageDrafts[id] || []).find(i => i.key === key); if (image) Object.assign(image, values) },
    pending(state, { id, value }) { Vue.set(state.pending, id, value) },
    remove(state, payload) {
      const { id, deleted = false } = typeof payload === 'object' ? payload : { id: payload }
      const removed = state.conversations.find(c => c.id === id)
      state.unreadTotal = Math.max(0, state.unreadTotal - (removed?.unread_count || 0))
      if (deleted) Vue.set(state.removedIds, id, true)
      state.removalVersion++
      state.conversations = state.conversations.filter(c => c.id !== id)
      if (state.activeId === id) state.activeId = null
      for (const image of state.imageDrafts[id] || []) releaseImage(image)
      for (const field of ['messages', 'drafts', 'resourceDrafts', 'imageDrafts', 'pending', 'before', 'hasMore']) Vue.delete(state[field], id)
    }
  },
  actions: {
    async refresh({ state, commit }, more = false) {
      const generation = state.generation
      const removalVersion = state.removalVersion
      const offset = more ? state.inboxOffset : 0
      const { data } = await api.get('chat/conversations', { params: { offset } })
      if (generation !== state.generation || removalVersion !== state.removalVersion) return
      data.conversations = data.conversations.filter(c => !state.removedIds[c.id])
      if (!more) {
        // Retain loaded older pages; single-conversation events refresh them individually.
        const incoming = new Set(data.conversations.map(c => c.id))
        commit('set', { conversations: [...data.conversations, ...state.conversations.filter(c => !incoming.has(c.id) && (c.id === state.activeId || state.inboxOffset > 50))] })
      } else data.conversations.forEach(c => commit('conversation', c))
      commit('set', { unreadTotal: data.unread_total, inboxMore: data.has_more, inboxOffset: data.next_offset, settings: data.settings || state.settings })
    },
    async directory({ state, commit }) {
      const generation = state.generation
      const { data } = await api.get('chat/directory')
      if (generation === state.generation) commit('set', { directory: data.users })
    },
    async open({ state, commit, dispatch }, id) {
      if (state.removedIds[id]) return
      commit('set', { open: true, connectedOpen: false, activeId: id, loading: true })
      const generation = state.generation
      try {
        const { data } = await api.get(`chat/conversations/${id}`)
        if (generation !== state.generation || state.removedIds[id]) return
        commit('conversation', data.conversation)
        await dispatch('loadMessages', { id })
      } catch (error) {
        if (generation === state.generation && error.response && [403,404].includes(error.response.status)) commit('remove',id)
        throw error
      } finally { if (generation === state.generation) commit('set', { loading: false }) }
    },
    async loadMessages({ state, commit }, { id, older = false }) {
      const generation = state.generation
      const { data } = await api.get(`chat/conversations/${id}/messages`, { params: older ? { before: state.before[id] } : {} })
      if (generation === state.generation && !state.removedIds[id]) commit('messages', { id, data, older })
    },
    async create({ state, commit, dispatch }, params) {
      const generation = state.generation
      const { data } = await api.post('chat/conversations', params)
      if (generation !== state.generation) return
      commit('conversation', data.conversation)
      await dispatch('open', data.conversation.id)
      return data.conversation
    },
    async addImages({ state, commit, dispatch }, { id, files }) {
      const generation = state.generation
      const selected = Array.from(files)
      const current = state.imageDrafts[id] || []
      if (current.length + selected.length > (state.settings.max_images || 10)) throw new Error('Vous pouvez joindre jusqu’à 10 images par message.')
      if (selected.some(file => !imageTypes.includes(file.type) || file.size > (state.settings.image_max_kb || 10240) * 1024)) throw new Error('Images JPEG, PNG, WebP ou GIF uniquement, 10 Mo maximum par image.')
      const images = selected.map(file => ({ key: uuid(), uploadId: uuid(), file, url: URL.createObjectURL(file), status: 'waiting', progress: 0, error: '', image: null, controller: null }))
      commit('imageDrafts', { id, images: [...current, ...images] })
      for (const image of images) {
        if (generation !== state.generation || state.removedIds[id]) return
        await dispatch('uploadImage', { id, key: image.key })
      }
    },
    async uploadImage({ state, commit }, { id, key }) {
      const draft = (state.imageDrafts[id] || []).find(i => i.key === key)
      if (!draft || draft.status === 'uploading' || draft.status === 'ready') return
      const generation = state.generation
      const controller = new AbortController()
      const current = () => generation === state.generation && !state.removedIds[id] && (state.imageDrafts[id] || []).some(i => i.key === key)
      commit('imageDraft', { id, key, values: { status: 'uploading', error: '', progress: 0, controller } })
      const form = new FormData(); form.append('image', draft.file); form.append('client_uuid', draft.uploadId)
      try {
        const { data } = await api.post(`chat/conversations/${id}/images`, form, { signal: controller.signal, onUploadProgress(event) {
          if (current()) commit('imageDraft', { id, key, values: { progress: Math.min(99, Math.round(event.loaded / (event.total || draft.file.size || 1) * 100)) } })
        } })
        if (current()) commit('imageDraft', { id, key, values: { status: 'ready', image: data.image, progress: 100, controller: null } })
        else if (generation === state.generation) api.delete(imageEndpoint(id, data.image.id)).catch(() => {})
      } catch (error) {
        if (!current()) return
        // An explicitly retired/expired reservation needs a new upload ID; network retries keep it.
        const uploadId = [410,503].includes(error.response?.status) ? uuid() : draft.uploadId
        commit('imageDraft', { id, key, values: { status: 'error', error: error.response?.data?.message || 'Envoi interrompu. Réessayez.', controller: null, uploadId } })
      }
    },
    async removeImage({ state, commit }, { id, key }) {
      const image = (state.imageDrafts[id] || []).find(i => i.key === key)
      if (!image) return
      releaseImage(image)
      commit('imageDrafts', { id, images: state.imageDrafts[id].filter(i => i.key !== key) })
      // If an interrupted upload has not reserved its row yet, the 24h sweep handles it.
      try { await api.delete(imageEndpoint(id, image.image?.id || image.uploadId)) } catch (error) {
        if (![404,410].includes(error.response?.status)) throw error
      }
    },
    async send({ state, commit, dispatch }, { id, body, replyId, resources = [] }) {
      const generation = state.generation
      const images = [...(state.imageDrafts[id] || [])]
      if (images.some(i => i.status !== 'ready')) throw new Error('Attendez la fin de l’envoi des images ou retirez celles en erreur.')
      const imageIds = images.map(i => i.image.id)
      const pending = state.pending[id]
      const request = pending && pending.body === body && pending.replyId === replyId && JSON.stringify(pending.resources || []) === JSON.stringify(resources) && JSON.stringify(pending.imageIds || []) === JSON.stringify(imageIds) ? pending : { uuid: uuid(), body, replyId, resources, imageIds }
      commit('pending', { id, value: request })
      const { data } = await api.post(`chat/conversations/${id}/messages`, { body, resources, image_ids: imageIds, client_uuid: request.uuid, reply_to_message_id: replyId || null })
      if (generation !== state.generation || state.removedIds[id]) return
      commit('message', data.message)
      commit('pending', { id, value: null })
      commit('draft', { id, text: '' })
      commit('resourceDrafts', { id, resources: [] })
      images.forEach(releaseImage)
      const sent = new Set(images.map(i => i.key))
      commit('imageDrafts', { id, images: (state.imageDrafts[id] || []).filter(i => !sent.has(i.key)) })
      await dispatch('refresh')
    },
    async read({ state, getters, commit, dispatch }) {
      const generation = state.generation
      const last = getters.activeMessages[getters.activeMessages.length - 1]
      const active = getters.active
      if (!state.open || !last || !active || document.visibilityState !== 'visible' || Number(active.last_read_message_id) >= last.id) return
      const id = active.id
      const { data } = await api.post(`chat/conversations/${id}/read`, { message_id: last.id })
      if (generation !== state.generation || state.removedIds[id]) return
      commit('conversation', { ...active, last_read_message_id: data.last_read_message_id, unread_count: 0 })
      await dispatch('refresh')
    },
    async deleteConversation({ state, commit }, id) {
      const generation = state.generation
      await api.delete(`chat/conversations/${id}`)
      if (generation === state.generation) commit('remove', { id, deleted: true })
    },
    async favorite({ state, commit, dispatch }, conversation) {
      const generation = state.generation
      const { data } = await api.patch(`chat/conversations/${conversation.id}/favorite`, { is_favorite: !conversation.is_favorite })
      if (generation !== state.generation) return
      commit('conversation', data.conversation)
      await dispatch('refresh')
    },
    async event({ state, commit, dispatch, rootGetters }, event) {
      const generation = state.generation
      if (event.action === 'conversation_deleted') { commit('remove', { id: event.conversation_id, deleted: true }); await dispatch('refresh'); return }
      if (state.removedIds[event.conversation_id]) return
      if (event.action === 'read' && event.conversation_id !== state.activeId && Number(event.sender_user_id) !== Number(rootGetters.currentUser.uuid)) return
      try {
        const { data } = await api.get(`chat/conversations/${event.conversation_id}`)
        if (generation !== state.generation) return
        commit('conversation', data.conversation)
        if (['message_updated','message_deleted'].includes(event.action) && event.message_id && (state.messages[event.conversation_id] || []).some(m => m.id === Number(event.message_id))) {
          const response = await api.get(`chat/conversations/${event.conversation_id}/messages/${event.message_id}`)
          if (generation === state.generation) commit('message', response.data.message)
        }
        if (state.open && state.activeId === event.conversation_id && event.action !== 'read') await dispatch('loadMessages', { id: event.conversation_id })
      } catch (error) {
        if (generation !== state.generation) return
        if (error.response && [403, 404].includes(error.response.status)) {
          commit('remove', event.conversation_id)
        } else throw error
      }
      if (event.action !== 'read' || Number(event.sender_user_id) === Number(rootGetters.currentUser.uuid)) await dispatch('refresh')
    }
  }
}
